import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goodsReceiptsApi } from '../goodsReceiptApi';
import type { GoodsReceiptDetail } from '../goodsReceiptTypes';

/**
 * Abandon a delivery that was never registered.
 *
 * <b>A FORM rather than a `<ConfirmDialog>`, because it COLLECTS.</b> The reason is required and
 * is the only thing the next reader of the order is told about why the goods never arrived
 * against it — and `<ConfirmDialog>` cannot ask for a value. Stacking one over a form would break
 * "one modal, never stacked", so the reason and the confirm tick sit on the one form together.
 *
 * <b>Only reachable before registration.</b> A registered receipt's stock exists, and taking it
 * back out is a stock adjustment with its own reason — the server refuses this outright, and the
 * profile does not offer it.
 */
export function CancelDeliveryModal({
  open,
  receiptId,
  receiptNumber,
  onClose,
  onSaved,
}: {
  open: boolean;
  receiptId: string;
  receiptNumber: string;
  onClose: () => void;
  onSaved: (receipt: GoodsReceiptDetail) => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    setReason('');
    setConfirmed(false);
    setError(null);
  }, [open]);

  const canSave = reason.trim() !== '' && confirmed && !saving;

  async function save() {
    setSaving(true);
    setError(null);

    try {
      onSaved(await goodsReceiptsApi.cancel(receiptId, { reason: reason.trim() }));
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('The delivery could not be cancelled.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'cancel-delivery' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cancel ${receiptNumber}`}
      size="lg"
      id="cancel-delivery-modal"
      confirm={{
        id: 'cancel-delivery-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="cancel-delivery-close" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip
            disabledReason={
              canSave ? null : reason.trim() === '' ? 'Say why the delivery is being abandoned.' : 'Tick the box to confirm.'
            }
            block={false}
          >
            <Button
              id="cancel-delivery-submit"
              variant="danger"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Cancelling"
            >
              Cancel delivery
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Textarea
          id="cancel-delivery-reason"
          label="Why it is being abandoned"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Keyed against the wrong order, delivery refused at the door…"
          description="Required. The next person to look at the purchase order will want to know why nothing arrived against it."
          disabled={saving}
          error={error?.fieldError('reason')}
        />

        <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <p>
            <b>Nothing is unwound, because nothing was created.</b> An uninspected delivery has
            written no stock, no ledger line and no change to the purchase order — its lines are
            still open for the supplier to deliver against.
          </p>

          <p>
            <b>Cancelling is not deleting.</b> The receipt stays in the register saying what
            happened. Deleting is for one keyed against the wrong order entirely.
          </p>
        </div>
      </div>
    </Modal>
  );
}
